import { useEffect } from "react";

export const useDataViewPageController = ({
  activeDataset,
  submittedQuery,
  isDetailPanelOpen,
  selectedRecordId,
  records,
  selectedRows,
  pagination,
  batchOptions,
  selectedBatchValue,
  isSearchMode,
  isBulkDownloading,
  isAdminMode,
  detailPanelPosition,
  detailPanelPositions,
  listLoading,
  setSelectedRecordId,
  setIsDetailPanelOpen,
  setSelectedBatchValue,
  setDownloadError,
  setSaveRecordError,
  setCompanyBulkError,
  setSelectedRowIds,
  handleBulkDownload,
  clearDetailState,
  beginDetailLoading,
}) => {
  const showPagination = pagination.page > 1 || pagination.has_more;
  const showRightDetailPanel =
    isDetailPanelOpen && detailPanelPosition === detailPanelPositions.RIGHT;
  const showTopDetailPanel =
    isDetailPanelOpen && detailPanelPosition === detailPanelPositions.TOP;
  const showMassDownloadControls = isAdminMode || selectedRows.length > 0;

  const bulkDownloadDisabled = isSearchMode
    ? listLoading || (!records.length && !selectedRows.length)
    : listLoading || isBulkDownloading || (!selectedRows.length && !selectedBatchValue);

  const primaryDownloadLabel = selectedRows.length
    ? `Download Selected (${selectedRows.length})`
    : isSearchMode
      ? "Download Current Results"
      : isBulkDownloading
        ? "Downloading..."
        : "Bulk Download CSV";

  const handleRowClick = (recordId) => {
    if (activeDataset === "company") {
      beginDetailLoading?.();
    }
    setSelectedRecordId(recordId);
    setIsDetailPanelOpen(true);
  };

  const handleCloseDetailPanel = () => {
    setIsDetailPanelOpen(false);
    setSelectedRecordId(null);
    clearDetailState?.();
  };

  const handleBulkDownloadClick = () =>
    handleBulkDownload({
      records,
      selectedRows,
      batchOptions,
      selectedBatchValue,
      isSearchMode,
    });

  useEffect(() => {
    if (!selectedRecordId) {
      setIsDetailPanelOpen(false);
    }
  }, [selectedRecordId, setIsDetailPanelOpen]);

  useEffect(() => {
    if (!batchOptions.length || isSearchMode) {
      setSelectedBatchValue("");
      return;
    }

    if (!batchOptions.some((option) => option.value === selectedBatchValue)) {
      setSelectedBatchValue(batchOptions[0].value);
    }
  }, [batchOptions, isSearchMode, selectedBatchValue, setSelectedBatchValue]);

  useEffect(() => {
    setDownloadError("");
  }, [activeDataset, isSearchMode, pagination.page, setDownloadError]);

  useEffect(() => {
    setCompanyBulkError("");
  }, [activeDataset, pagination.page, submittedQuery, setCompanyBulkError]);

  useEffect(() => {
    setSaveRecordError("");
  }, [activeDataset, selectedRecordId, setSaveRecordError]);

  useEffect(() => {
    setSelectedRowIds([]);
  }, [activeDataset, submittedQuery, pagination.page, setSelectedRowIds]);

  useEffect(() => {
    if (!showTopDetailPanel || typeof window === "undefined") {
      return undefined;
    }

    const handleKeyDown = (event) => {
      if (event.key === "Escape") {
        setIsDetailPanelOpen(false);
      }
    };

    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    window.addEventListener("keydown", handleKeyDown);

    return () => {
      document.body.style.overflow = previousOverflow;
      window.removeEventListener("keydown", handleKeyDown);
    };
  }, [showTopDetailPanel, setIsDetailPanelOpen]);

  return {
    showPagination,
    bulkDownloadDisabled,
    primaryDownloadLabel,
    showRightDetailPanel,
    showTopDetailPanel,
    showMassDownloadControls,
    handleRowClick,
    handleCloseDetailPanel,
    handleBulkDownloadClick,
  };
};
